import {
  getCurrentEntityPendingChanges,
  getFormattedFullAddress,
} from 'app/pages/loanApplication/LoanApplication.utils';
import { crmContactApi, crmTagApi } from 'app/services/api';
import _ from 'lodash';

import { ADDRESS_FIELDS_TO_COUNT } from 'constants/loanApplication';
import {
  HistoryTypeToPendingChangesTypeMap,
  PendingChangesActionType,
} from 'constants/pendingChanges';
import { getConditionalArray } from 'utils/data';
import { getPersonFullName } from 'utils/loanApplication';
import { mergePendingChanges } from 'utils/pendingChanges';

import {
  AGENT_CONTACT_LIFECYCLE_STAGE,
  AGENT_TAG_NAME,
  agentAndSellerModifiedFieldsToExclude,
  MAX_AGENTS_COUNT,
  TableDataType,
} from './AgentAndSeller.constants';

export const createNewTableData = type => {
  return {
    id: _.uniqueId(),
    // * This flag is needed for cases when we add new entity but not save it
    isNewlyAdded: true,
    isAdded: true,
    entityType: type,
  };
};

export const orderTableData = ({ data, order }) => _.orderBy(data, [order.id], [order.direction]);

export const getInitialValuesMaps = ({ initialEntitySideData }) => {
  const sellingAgentInitialValuesMap = initialEntitySideData.sellingAgents
    ? _.keyBy(initialEntitySideData.sellingAgents, 'id')
    : {};
  const listingAgentInitialValuesMap = initialEntitySideData.listingAgents
    ? _.keyBy(initialEntitySideData.listingAgents, 'id')
    : {};
  const sellerInfoInitialValuesMap = initialEntitySideData.sellers
    ? _.keyBy(initialEntitySideData.sellers, 'id')
    : {};

  return { sellingAgentInitialValuesMap, listingAgentInitialValuesMap, sellerInfoInitialValuesMap };
};

const getAgentData = ({ form, initialData }) => {
  return {
    id: initialData.id,
    type: initialData.entityType,
    name: form.name,
    office: form.office,
    phone: form.phone,
    fax: form.fax,
    email: form.email,
    agentNmlsId: form.agentNmlsId,
    agentStateLicenseId: form.agentStateLicenseId,
    companyNmlsId: form.companyNmlsId,
    companyStateLicenseId: form.companyStateLicenseId,
    street: form.street,
    unit: form.unit,
    city: form.city,
    state: form.state?.value,
    zip: form.zip,
    isAdded: initialData.isAdded,
  };
};

const getSellerData = ({ form, initialData }) => {
  return {
    id: initialData.id,
    type: initialData.entityType,
    sellerName: getPersonFullName(form),
    firstName: form.firstName,
    middleName: form.middleName,
    lastName: form.lastName,
    email: form.email,
    phone: form.phone,
    companyName: form.companyName,
    street: form.street,
    unit: form.unit,
    city: form.city,
    state: form.state?.value,
    zip: form.zip,
    isAdded: initialData.isAdded,
    fullAddress: getFormattedFullAddress({
      address: form,
    }),
  };
};

export const normalizeFormData = ({ form, initialData }) => {
  switch (initialData.entityType) {
    case TableDataType.SELLING_AGENT:
    case TableDataType.LISTING_AGENT:
      return getAgentData({ form, initialData });

    case TableDataType.SELLER:
      return getSellerData({ form, initialData });

    default:
      return null;
  }
};

export const prepareModifiedFields = ({ currentData, pendingChangesData, initialValuesMap }) =>
  currentData.map(values => {
    const pendingChanges = pendingChangesData[values.id];
    const currentPendingChanges = getCurrentEntityPendingChanges({
      initialValues: initialValuesMap[values.id],
      currentValues: values,
    });

    if (!pendingChanges && _.isEmpty(currentPendingChanges)) {
      return {
        ...values,
        pendingChangesActionType: PendingChangesActionType.NO_CHANGES,
      };
    }

    const mergedPendingChanges = _.omit(
      mergePendingChanges(pendingChanges, currentPendingChanges),
      ['isSeparateChange'],
    );

    const modifiedFields = Object.keys(mergedPendingChanges);

    const isFullAddressChanged = !!_.intersection(modifiedFields, ADDRESS_FIELDS_TO_COUNT).length;

    const normalizedModifiedFields = modifiedFields.reduce(
      (acc, field) => {
        const pendingChange = mergedPendingChanges[field];

        if (pendingChange.newValue === pendingChange.initialValue) {
          return acc;
        }

        if (agentAndSellerModifiedFieldsToExclude.includes(field)) {
          return acc;
        }

        acc.push(_.upperFirst(field));

        return acc;
      },
      getConditionalArray(isFullAddressChanged && values.type === TableDataType.SELLER, [
        'FullAddress',
      ]),
    );

    const historyActionType = modifiedFields[0]
      ? mergedPendingChanges[modifiedFields[0]]?.actionType
      : '';
    const pendingChangesActionType = HistoryTypeToPendingChangesTypeMap[historyActionType];

    return {
      ...values,
      modifiedFields: normalizedModifiedFields,
      pendingChangesActionType,
    };
  });

export const checkIsAgentsMaxCount = agents =>
  agents.filter(
    agent =>
      !agent.isDeleted && agent.pendingChangesActionType !== PendingChangesActionType.DELETED,
  ).length >= MAX_AGENTS_COUNT;

// Normalize the paged-or-bare-array envelope that /api/contacts returns.
const normalizeContactItems = data => {
  if (Array.isArray(data)) {
    return data;
  }

  return data?.items ?? [];
};

// CRM contact id may be a bare string or a { value } wrapper.
const getContactId = contact => contact?.id?.value ?? contact?.id ?? null;

// Split a "First Last" agent name into first/last parts for CRM contact creation.
const splitAgentName = name => {
  const trimmed = (name ?? '').trim();
  const spaceIdx = trimmed.indexOf(' ');

  if (spaceIdx === -1) {
    return { firstName: trimmed, lastName: '' };
  }

  return {
    firstName: trimmed.slice(0, spaceIdx),
    lastName: trimmed.slice(spaceIdx + 1),
  };
};

/**
 * Build the CRM createContact request body from saved agent fields.
 *
 * @param {object} agentData - the normalized agent object from normalizeFormData
 * @returns {object} CRM contact DTO
 */
const buildAgentContactPayload = agentData => {
  const { firstName, lastName } = splitAgentName(agentData.name);

  return {
    firstName,
    lastName,
    primaryEmail: agentData.email || null,
    primaryPhone: agentData.phone || null,
    lifecycleStage: AGENT_CONTACT_LIFECYCLE_STAGE,
    licenseNumber: agentData.agentNmlsId || null,
    stateLicenseId: agentData.agentStateLicenseId || null,
    office: agentData.office || null,
  };
};

/**
 * Find an existing CRM contact whose primaryEmail matches.  Returns null when
 * no match is found or on any fetch error.
 *
 * @param {string} email
 * @returns {Promise<object|null>}
 */
const findContactByEmail = async email => {
  try {
    const { data } = await crmContactApi.getContacts({ search: email, pageSize: 20 });
    const items = normalizeContactItems(data);
    const normalizedEmail = email.trim().toLowerCase();

    return (
      items.find(c => (c.primaryEmail ?? c.email ?? '').trim().toLowerCase() === normalizedEmail) ??
      null
    );
  } catch {
    return null;
  }
};

/**
 * Find an existing CRM tag by name, creating it if absent.
 * Returns the tag id, or null on any error.
 *
 * @param {string} tagName
 * @returns {Promise<string|null>}
 */
const getOrCreateAgentTagId = async tagName => {
  try {
    const { data: tagsData } = await crmTagApi.getTags({ search: tagName });
    const tags = Array.isArray(tagsData) ? tagsData : (tagsData?.items ?? []);
    const existing = tags.find(t => (t.name ?? '').trim().toLowerCase() === tagName.toLowerCase());

    if (existing) {
      return existing.id ?? null;
    }

    const { data: created } = await crmTagApi.createTag({ name: tagName });

    return created?.id ?? null;
  } catch {
    return null;
  }
};

/**
 * Find the agent tag id for the picker (read-only; does NOT create the tag).
 * Returns null when the tag doesn't exist yet or on any error.
 *
 * @returns {Promise<string|null>}
 */
export const findAgentTagId = async () => {
  try {
    const { data: tagsData } = await crmTagApi.getTags({ search: AGENT_TAG_NAME });
    const tags = Array.isArray(tagsData) ? tagsData : (tagsData?.items ?? []);
    const found = tags.find(
      t => (t.name ?? '').trim().toLowerCase() === AGENT_TAG_NAME.toLowerCase(),
    );

    return found?.id ?? null;
  } catch {
    return null;
  }
};

const resolveContactId = async (existing, agentData) => {
  if (existing) {
    return getContactId(existing);
  }

  const payload = buildAgentContactPayload(agentData);
  const { data: created } = await crmContactApi.createContact(payload);

  return getContactId(created);
};

/**
 * Fire-and-forget: persist a saved agent to CRM and assign the agent tag.
 * Requires at least an email + at least one of first/last name.
 * Never throws — all errors are swallowed so the caller's local Add is never blocked.
 *
 * @param {object} agentData - normalized agent object from normalizeFormData
 */
export const persistAgentToCrm = async agentData => {
  const email = agentData?.email?.trim();
  const { firstName, lastName } = splitAgentName(agentData?.name);

  // CRM contact creation requires a first AND last name + email; skip otherwise.
  if (!email || !firstName || !lastName) {
    return;
  }

  try {
    const tagId = await getOrCreateAgentTagId(AGENT_TAG_NAME);
    const existing = await findContactByEmail(email);
    const contactId = await resolveContactId(existing, agentData);

    if (contactId && tagId) {
      await crmContactApi.assignContactTag({ contactId, tagId });
    }
  } catch {
    // Intentionally swallowed — CRM sync is fire-and-forget.
  }
};
